import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, ChevronRight, Trash2 } from 'lucide-react';
import { useTrips, useCreateTrip, useDeleteTrip } from '../hooks/useTravelBuddy';
import { QuickTripModal } from '../components/QuickTripModal';
import { useTravelBuddyUi } from '../store/travelBuddyUiStore';
import * as T from '../ui/inlineTheme';
import { sf } from '../../themeConfig/sisuForgeTokens';

export function Dashboard() {
  const [modal, setModal] = useState(false);
  const setModalOpen = useTravelBuddyUi((s) => s.setModalOpen);

  useEffect(() => {
    setModalOpen(modal);
    return () => setModalOpen(false);
  }, [modal, setModalOpen]);
  const { data: trips = [], isLoading } = useTrips();
  const createTrip = useCreateTrip();
  const deleteTrip = useDeleteTrip();
  const navigate = useNavigate();

  const onDeleteTrip = (e: React.MouseEvent, id: string, name: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      !window.confirm(
        `Delete trip “${name}”? This removes receipts, routes, and packing lists for this trip. This cannot be undone.`
      )
    ) {
      return;
    }
    deleteTrip.mutate(id);
  };

  const onQuick = (name: string) => {
    createTrip.mutateAsync({ name }).then((t) => {
      setModal(false);
      navigate(`/travel-buddy/trip/${t.id}`);
    });
  };

  return (
    <div style={T.page}>
      <div className="tb-page-header" style={T.rowBetween}>
        <div>
          <h1 style={T.h1}>Trips</h1>
          <p style={T.bodyMuted}>
            Receipts, packing lists, and expense summaries per trip.
          </p>
        </div>
        <button
          type="button"
          className="sf-btn-primary"
          onClick={() => setModal(true)}
          style={{
            ...T.btnPrimary,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          <Plus size={20} aria-hidden />
          Quick trip
        </button>
      </div>

      {isLoading && (
        <p style={{ ...T.bodyMuted, marginTop: 0 }}>Loading…</p>
      )}

      {!isLoading && trips.length === 0 && (
        <div style={T.card}>
          <p style={{ margin: 0, fontFamily: T.font, fontSize: 16, color: sf.text }}>
            No trips yet.
          </p>
          <button type="button" className="tb-link-ghost" onClick={() => setModal(true)}>
            Create your first trip
          </button>
        </div>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {trips.map((t) => (
          <li
            key={t.id}
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 8,
              borderRadius: 12,
              border: `1px solid ${sf.border}`,
              backgroundColor: sf.surfaceElevated,
              overflow: 'hidden',
            }}
          >
            <Link
              className="tb-list-link"
              to={`/travel-buddy/trip/${t.id}`}
              style={{ ...T.listItemLink, flex: 1, border: 'none', borderRadius: 0 }}
            >
              <div>
                <p style={{ margin: 0, fontFamily: T.font, fontSize: 17, fontWeight: 600, color: '#E5E7EB' }}>
                  {t.name}
                </p>
                <p style={{ margin: '6px 0 0', fontFamily: T.font, fontSize: 14, color: '#9CA3AF' }}>
                  {new Date(t.created_at).toLocaleDateString()}
                </p>
              </div>
              <ChevronRight size={22} color="#9CA3AF" aria-hidden />
            </Link>
            <button
              type="button"
              title="Delete trip"
              aria-label={`Delete trip ${t.name}`}
              disabled={deleteTrip.isPending}
              onClick={(e) => onDeleteTrip(e, t.id, t.name)}
              style={{
                flexShrink: 0,
                width: 48,
                border: 'none',
                borderLeft: `1px solid ${sf.border}`,
                background: 'rgba(127, 29, 29, 0.25)',
                color: '#fca5a5',
                cursor: deleteTrip.isPending ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <Trash2 size={20} strokeWidth={2} aria-hidden />
            </button>
          </li>
        ))}
      </ul>

      <QuickTripModal
        open={modal}
        onClose={() => setModal(false)}
        onSubmit={onQuick}
        loading={createTrip.isPending}
      />
    </div>
  );
}
